import useBundleFilters from "core/hooks/useBundleFilters";
import { useMemo } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faUserMagnifyingGlass,
  faChildren,
  faChartPie,
  faHousePersonReturn,
  faChartSimpleHorizontal,
  faUserDoctor,
  faHospitalUser,
  faPeopleArrows,
  faChartUser,
  faFilesMedical,
  faUsers,
  faDna,
  faMessageDollar,
  faChartMixedUpCircleDollar,
} from "@fortawesome/pro-regular-svg-icons";
import { useLocation } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import useNavigate from "core/hooks/useNavigate";

const GeneralMenu = styled.div`
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MenuHeader = styled.div`
  height: fit-content;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
`;

const BundleMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 16px;
`;

const ItemMenu = styled.div`
  border-radius: 5px;
  padding: 7px 10px;
  height: fit-content;
  display: flex;
  gap: 8px;
  align-items: center;
  color: #000000;

  &:hover {
    background-color: #ddcdef;
  }

  &.selected {
    background-color: #ddcdef;
    cursor: default;
  }
`;

const BundleItems = styled.div``;

const LineSeparator = styled.div`
  border-bottom: 1px solid #dadde1;
`;

const SummaryMenu = styled.div`
  border-radius: 5px;
  padding: 7px 10px;
  height: 34px;
  display: flex;
  gap: 8px;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background-color: #ddcdef;
  }

  &.selected {
    background-color: #ddcdef;
    cursor: default;
  }
`;

const MenuTitle = styled.div`
  color: #000000;
  border-radius: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  white-space: nowrap;
  cursor: pointer;
  line-height: 13px;
  font-size: 13px;
`;

const PRODUCT_IDS = {
  IDENTIFIED_CONSUMERS: "identified_consumers",
  DE_IDENTIFIED_CONSUMERS: "deidentified_consumers",
  PROPENSITY_MODELS: "propensity_models",
  NEW_MOVERS: "new_movers",
  MARKET_VOLUME_LITE: "market_volume_lite",
  PROVIDER_AFFILIATIONS: "provider_affiliations",
  PROVIDER_PATHWAYS: "provider_pathways",
  PROVIDER_BENCHMARKS: "provider_benchmarks",
  MARKET_VOLUME: "market_volume",
  CLAIM_VOLUME: "claim_volume",
  PATIENT_VOLUME: "patient_volume",
  MARKET_VOLUME_ONCOLOGY: "market_volume_oncology",
  PRICE_TRANSPARENCY: "price_transparency",
  MARKET_FORECAST: "market_forecast",
  PROVIDER_DIRECTORY: "provider_directory",
};

const PRODUCT_ICON = {
  [PRODUCT_IDS.IDENTIFIED_CONSUMERS]: faUserMagnifyingGlass,
  [PRODUCT_IDS.DE_IDENTIFIED_CONSUMERS]: faChildren,
  [PRODUCT_IDS.PROPENSITY_MODELS]: faChartPie,
  [PRODUCT_IDS.NEW_MOVERS]: faHousePersonReturn,
  [PRODUCT_IDS.MARKET_VOLUME_LITE]: faChartSimpleHorizontal,
  [PRODUCT_IDS.PROVIDER_AFFILIATIONS]: faHospitalUser,
  [PRODUCT_IDS.PROVIDER_PATHWAYS]: faPeopleArrows,
  [PRODUCT_IDS.PROVIDER_BENCHMARKS]: faChartUser,
  [PRODUCT_IDS.MARKET_VOLUME]: faChartSimpleHorizontal,
  [PRODUCT_IDS.CLAIM_VOLUME]: faFilesMedical,
  [PRODUCT_IDS.PATIENT_VOLUME]: faUsers,
  [PRODUCT_IDS.MARKET_VOLUME_ONCOLOGY]: faDna,
  [PRODUCT_IDS.PRICE_TRANSPARENCY]: faMessageDollar,
  [PRODUCT_IDS.MARKET_FORECAST]: faChartMixedUpCircleDollar,
  [PRODUCT_IDS.PROVIDER_DIRECTORY]: faUserDoctor,
};

const ProductBundleMenu = () => {
  const { visibleProducts, selectedBundles } = useBundleFilters();
  const navigate = useNavigate();
  const location = useLocation();

  const locationIsConfigure = useMemo(
    () =>
      location.pathname
        .split("/")
        .filter((p) => !!p)
        .reverse()[0] === "configure",
    [location]
  );

  return (
    <div>
      <GeneralMenu>
        <MenuHeader>General</MenuHeader>
        <LineSeparator />
        <SummaryMenu
          className={locationIsConfigure && "selected"}
          // Relative to the `organization/:orgId` route that renders this menu
          // — not to the product URL we are currently on. A leading "../" walks
          // off the org route entirely and lands on the 404.
          onClick={() => navigate("product/configure")}
        >
          <FontAwesomeIcon icon={faList} />
          <MenuTitle>Summary</MenuTitle>
        </SummaryMenu>
      </GeneralMenu>
      <AnimatePresence>
        {selectedBundles.map((bundle) => {
          return (
            <motion.div
              key={bundle.id}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  duration: 0.25,
                  ease: "linear",
                },
              }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0.15,
                  ease: "linear",
                },
              }}
              layout
            >
              <BundleMenu>
                <MenuHeader>{bundle.name}</MenuHeader>
                <LineSeparator />
                {visibleProducts
                  .filter((product) => product.meta.bundle.id === bundle.id)
                  .map((product) => {
                    return (
                      <BundleItems key={product.id}>
                        <ItemMenu
                          className={
                            location.pathname
                              .split("/")
                              .filter((p) => !!p)
                              .reverse()[0] === `${product.page.path}` &&
                            "selected"
                          }
                          onClick={() => {
                            const productRoute = `product/${bundle.path}/${product.page.path}`;
                            navigate(productRoute);
                          }}
                        >
                          <FontAwesomeIcon
                            icon={PRODUCT_ICON[product.id]}
                            style={{
                              width: 20,
                              height: 16,
                            }}
                          />
                          <MenuTitle>{product.meta.name}</MenuTitle>
                        </ItemMenu>
                      </BundleItems>
                    );
                  })}
              </BundleMenu>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ProductBundleMenu;
